import React from 'react';
import { Box, Typography } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, ReferenceDot, ReferenceLine } from 'recharts';

/**
 * AdoptionCurveChart - Bell curve visualization for AI adoption stages
 *
 * Maps scores to Technology Adoption Life Cycle positions:
 * - Laggards (1.0-1.8) → Left tail
 * - Late Majority (1.8-2.6) → Left of center
 * - Early Majority (2.6-3.4) → Center
 * - Early Adopters (3.4-4.2) → Right of center
 * - Innovators (4.2-5.0) → Right tail
 */

// Adoption stage definitions with boundaries
const ADOPTION_STAGES = [
  { id: 'laggards', label: 'LAGGARDS', minScore: 1.0, maxScore: 1.8, xStart: 0, xEnd: 20 },
  { id: 'late_majority', label: 'LATE MAJORITY', minScore: 1.8, maxScore: 2.6, xStart: 20, xEnd: 40 },
  { id: 'early_majority', label: 'EARLY MAJORITY', minScore: 2.6, maxScore: 3.4, xStart: 40, xEnd: 60 },
  { id: 'early_adopters', label: 'EARLY ADOPTERS', minScore: 3.4, maxScore: 4.2, xStart: 60, xEnd: 80 },
  { id: 'innovators', label: 'INNOVATORS', minScore: 4.2, maxScore: 5.0, xStart: 80, xEnd: 100 },
];

// Convert score (0-5) to X position (0-100)
// Uses score/5 formula to match the displayed percentage calculation in SummarySection
const scoreToXPosition = (score) => {
  const clampedScore = Math.max(0, Math.min(5, score));
  return (clampedScore / 5) * 100;
};

// Get adoption stage for a given score
export const getAdoptionStage = (score) => {
  for (const stage of ADOPTION_STAGES) {
    if (score >= stage.minScore && score < stage.maxScore) {
      return stage;
    }
  }
  // Handle edge case of exactly 5.0
  if (score >= 5.0) return ADOPTION_STAGES[4];
  return ADOPTION_STAGES[0];
};

// Generate gaussian bell curve data points
const generateBellCurveData = () => {
  const data = [];
  const mean = 50;  // Center of the curve
  const stdDev = 20; // Standard deviation for spread

  for (let x = 0; x <= 100; x += 1) {
    // Gaussian formula
    const y = Math.exp(-Math.pow(x - mean, 2) / (2 * Math.pow(stdDev, 2)));
    data.push({ x, y: y * 100 }); // Scale y to 0-100 for visibility
  }
  return data;
};

// Get Y value on the bell curve for a given X position
const getYForX = (xPos, curveData) => {
  const nearestPoint = curveData.find(d => Math.abs(d.x - xPos) < 1);
  return nearestPoint ? nearestPoint.y : 50;
};

export default function AdoptionCurveChart({
  score,
  benchmarkScore = null,
  height = 280,
  showLabels = true
}) {
  const curveData = generateBellCurveData();

  // Calculate positions for markers
  const orgXPos = scoreToXPosition(score || 1);
  const benchmarkXPos = benchmarkScore ? scoreToXPosition(benchmarkScore) : null;
  const orgYPos = getYForX(orgXPos, curveData);

  // Get current stage for the org
  const currentStage = getAdoptionStage(score || 1);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* Stage Labels */}
      {showLabels && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          mb: 1
        }}>
          {ADOPTION_STAGES.map((stage) => (
            <Typography
              key={stage.id}
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                fontWeight: stage.id === currentStage.id ? 'bold' : 'normal',
                color: stage.id === currentStage.id ? 'rgb(1, 28, 45)' : '#666',
                textAlign: 'center',
                width: '20%',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {stage.label}
            </Typography>
          ))}
        </Box>
      )}

      {/* Bell Curve Chart */}
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        p: 2,
        position: 'relative'
      }}>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart
            data={curveData}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient id="bellCurveGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="rgb(1, 28, 45)" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="rgb(1, 28, 45)" stopOpacity={0.3}/>
              </linearGradient>
            </defs>
            <XAxis
              dataKey="x"
              type="number"
              domain={[0, 100]}
              hide
            />
            <YAxis
              type="number"
              domain={[0, 110]}
              hide
            />
            <Area
              type="monotone"
              dataKey="y"
              stroke="rgb(1, 28, 45)"
              strokeWidth={2}
              fill="url(#bellCurveGradient)"
              isAnimationActive={false}
            />

            {/* Benchmark marker (vertical line) - only shown when we have real data */}
            {benchmarkXPos !== null && (
              <ReferenceLine
                x={benchmarkXPos}
                stroke="rgb(1, 28, 45)"
                strokeWidth={2}
                strokeDasharray="4 4"
              />
            )}

            {/* Organization score marker (highlighted orange) */}
            {score && (
              <ReferenceDot
                x={orgXPos}
                y={orgYPos + 8}
                r={10}
                fill="#F97316"
                stroke="#EA580C"
                strokeWidth={2}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>

        {/* Legend */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          mt: 1,
          pt: 1,
          borderTop: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#F97316',
              border: '2px solid #EA580C'
            }} />
            <Typography variant="caption" color="text.secondary">
              You ({currentStage.label.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')})
            </Typography>
          </Box>
          {benchmarkScore && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 16,
                height: 2,
                backgroundImage: 'repeating-linear-gradient(90deg, rgb(1,28,45), rgb(1,28,45) 3px, transparent 3px, transparent 6px)'
              }} />
              <Typography variant="caption" color="text.secondary">
                Average score ({Math.round((benchmarkScore / 5) * 100)}%)
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
